import type { NodeIdGenerator } from './types';

/**
 * Create a new ID generator for CraftJS nodes
 *
 * Generates unique IDs that are safe to use in CraftJS node dictionaries.
 * Uses crypto.randomUUID() when available, with a fallback for older environments.
 */
export const createIdGenerator = (): NodeIdGenerator => {
  const usedIds = new Set<string>();
  let counter = 0;

  const generate = (prefix?: string): string => {
    let id: string;

    // Try crypto.randomUUID() first (available in modern browsers and Node.js 19+)
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      id = crypto.randomUUID();
    } else {
      // Fallback: generate a pseudo-random ID
      id = generateFallbackId(prefix, counter++);
    }

    // Ensure uniqueness within this generator instance
    while (usedIds.has(id)) {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        id = crypto.randomUUID();
      } else {
        id = generateFallbackId(prefix, counter++);
      }
    }

    usedIds.add(id);
    return id;
  };

  const exists = (id: string): boolean => {
    return usedIds.has(id);
  };

  return { generate, exists };
};

/**
 * Generate a fallback ID when crypto.randomUUID() is not available
 */
const generateFallbackId = (prefix: string | undefined, counter: number): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  const prefixPart = prefix ? `${prefix}_` : '';
  return `${prefixPart}${timestamp}_${counter}_${random}`;
};
